import { useState } from "react";
import { useParams } from "react-router-dom";
import { type Language, getPageTranslations } from "../lib/translations";
import { BookOpen } from "lucide-react";
import { useDynamicContent } from "../lib/dynamicContent";
import { BlogPostCard, type BlogPost } from "./BlogPostCard";
import { BlogPostDetail } from "./BlogPostDetail";
import { BlogSearchFilters } from "./BlogSearchFilters";

import { mdxAI_en, mdxAI_tr, mdxOS_en, mdxOS_tr } from "../content/blog";

function parseMDX(slug: string, rawText: string): BlogPost {
  const parts = rawText.split("---");
  if (parts.length < 3) {
    return { slug, title: slug, date: "", description: "", categories: [], tags: [], content: rawText };
  }
  const yaml = parts[1] || "";
  const content = parts.slice(2).join("---");

  const titleMatch = yaml.match(/title:\s*(.*)/);
  const dateMatch = yaml.match(/date:\s*(.*)/);
  const descMatch = yaml.match(/description:\s*(.*)/);
  const tagsMatch = yaml.match(/tags:\s*(.*)/);
  const catsMatch = yaml.match(/categories:\s*(.*)/);

  const titleVal = titleMatch?.[1];
  const dateVal = dateMatch?.[1];
  const descVal = descMatch?.[1];
  const tagsVal = tagsMatch?.[1];
  const catsVal = catsMatch?.[1];

  const words = content.trim().split(/\s+/).filter(w => w.length > 0).length;
  const readingTime = Math.max(1, Math.ceil(words / 230));

  return {
    slug,
    title: titleVal ? titleVal.replace(/['"]/g, "").trim() : slug,
    date: dateVal ? dateVal.trim() : "",
    description: descVal ? descVal.replace(/['"]/g, "").trim() : "",
    categories: catsVal ? catsVal.split(",").map(c => c.trim()).filter(Boolean) : [],
    tags: tagsVal ? tagsVal.split(",").map(t => t.trim()).filter(Boolean) : [],
    content,
    readingTime
  };
}

function getPosts(lang: Language): BlogPost[] {
  const sources = lang === "tr"
    ? [{ slug: "leveraging-ai", raw: mdxAI_tr }, { slug: "open-source-future", raw: mdxOS_tr }]
    : [{ slug: "leveraging-ai", raw: mdxAI_en }, { slug: "open-source-future", raw: mdxOS_en }];

  return sources.map((s) => parseMDX(s.slug, s.raw));
}

function mapDynamicBlogsToPosts(
  blogs: any[],
  categories: any[],
  tags: any[],
  lang: Language
): BlogPost[] {
  return blogs.map((b: any) => {
    const content = lang === "tr" ? b.content_tr : b.content_en;
    const words = content.trim().split(/\s+/).filter((w: string) => w.length > 0).length;
    const readingTime = Math.max(1, Math.ceil(words / 230));

    const tagIds = (lang === "tr" ? b.tags_tr : b.tags_en)
      .split(",")
      .map((t: string) => t.trim())
      .filter((t: string) => t.length > 0);

    const mappedTags = tagIds.map((tid: string) => {
      const found = tags.find((gt: any) => gt.id === tid);
      return found ? (lang === "tr" ? found.name_tr : found.name_en) : tid;
    });

    const catIds = (b.categories || "")
      .split(",")
      .map((c: string) => c.trim())
      .filter((c: string) => c.length > 0);

    const mappedCategories = catIds.map((cid: string) => {
      const found = categories.find((gc: any) => gc.id === cid);
      return found ? (lang === "tr" ? found.name_tr : found.name_en) : cid;
    });

    return {
      slug: b.slug,
      title: lang === "tr" ? b.title_tr : b.title_en,
      date: b.date,
      description: lang === "tr" ? b.description_tr : b.description_en,
      categories: mappedCategories,
      tags: mappedTags,
      content,
      readingTime
    };
  });
}

function BlogLoadingSkeleton({ slug }: { slug?: string }) {
  return (
    <div className="max-w-7xl mx-auto py-6 space-y-8 animate-pulse text-left">
      {slug ? (
        <div className="max-w-3xl mx-auto space-y-6">
          <div className="h-8 w-24 bg-muted rounded mb-8" />
          <div className="space-y-3 border-b border-border/80 pb-6">
            <div className="h-4 w-32 bg-muted rounded" />
            <div className="h-10 w-3/4 bg-muted rounded" />
            <div className="flex gap-2 pt-2">
              <div className="h-4 w-12 bg-muted rounded" />
              <div className="h-4 w-16 bg-muted rounded" />
            </div>
          </div>
          <div className="space-y-4 pt-4">
            <div className="h-4 w-full bg-muted rounded animate-pulse" />
            <div className="h-4 w-full bg-muted rounded animate-pulse" />
            <div className="h-4 w-5/6 bg-muted rounded animate-pulse" />
            <div className="h-4 w-full bg-muted rounded animate-pulse" />
          </div>
        </div>
      ) : (
        <>
          <div className="text-center md:text-left space-y-2 border-b border-border/60 pb-5">
            <div className="h-8 w-48 bg-muted rounded mx-auto md:mx-0" />
            <div className="h-4 w-96 bg-muted rounded mx-auto md:mx-0 mt-2" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="border border-border/60 bg-card/65 rounded-xl h-48 p-6 space-y-4">
                <div className="h-3 w-24 bg-muted rounded" />
                <div className="h-6 w-3/4 bg-muted rounded" />
                <div className="h-4 w-full bg-muted rounded" />
                <div className="flex gap-2 pt-2">
                  <div className="h-4 w-12 bg-muted rounded" />
                  <div className="h-4 w-16 bg-muted rounded" />
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function BlogTab() {
  const { lang = "en", slug } = useParams<{ lang: Language; slug?: string }>();
  const t = getPageTranslations(lang).blog;

  const { blogs: dynamicBlogs, categories: globalCategories, tags: globalTags, loading } = useDynamicContent();

  // Search & Filter States
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [appliedFilters, setAppliedFilters] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<string>("newest");

  if (loading) {
    return <BlogLoadingSkeleton slug={slug} />;
  }

  let posts: BlogPost[] = [];
  if (dynamicBlogs && dynamicBlogs.length > 0) {
    posts = mapDynamicBlogsToPosts(dynamicBlogs, globalCategories, globalTags, lang as Language);
  } else {
    posts = getPosts(lang as Language);
  }

  if (slug) {
    const post = posts.find((p) => p.slug === slug);
    if (post) {
      return <BlogPostDetail post={post} lang={lang} />;
    }
  }

  const allCategories = Array.from(
    new Set(posts.flatMap((p) => p.categories || []).filter(Boolean))
  );

  const toggleFilter = (cat: string) => {
    setSelectedFilters((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchQuery(searchInput);
  };

  const handleApplyFilters = () => {
    setAppliedFilters(selectedFilters);
  };

  const handleReset = () => {
    setSearchInput("");
    setSearchQuery("");
    setSelectedFilters([]);
    setAppliedFilters([]);
    setSortBy("newest");
  };

  const filteredPosts = posts.filter((post) => {
    const matchesSearch = searchQuery
      ? post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.content.toLowerCase().includes(searchQuery.toLowerCase())
      : true;

    const matchesCategory = appliedFilters.length > 0
      ? post.categories.some((cat) => appliedFilters.includes(cat))
      : true;

    return matchesSearch && matchesCategory;
  });

  const sortedPosts = [...filteredPosts].sort((a, b) => {
    if (sortBy === "alphabetical") {
      return a.title.localeCompare(b.title);
    }
    const timeA = a.date ? new Date(a.date).getTime() : 0;
    const timeB = b.date ? new Date(b.date).getTime() : 0;
    const cleanA = Number.isNaN(timeA) ? 0 : timeA;
    const cleanB = Number.isNaN(timeB) ? 0 : timeB;
    if (sortBy === "oldest") {
      return cleanA - cleanB;
    }
    return cleanB - cleanA;
  });

  const displayedPosts = sortedPosts.slice(0, 6);

  return (
    <div className="max-w-7xl mx-auto py-6 space-y-8 animate-fade-in">
      <BlogHeader title={t.title} subtitle={t.subtitle} />

      <BlogSearchFilters
        lang={lang}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        searchQuery={searchQuery}
        appliedFilters={appliedFilters}
        selectedFilters={selectedFilters}
        toggleFilter={toggleFilter}
        allCategories={allCategories}
        handleSearchSubmit={handleSearchSubmit}
        handleApplyFilters={handleApplyFilters}
        handleReset={handleReset}
        sortBy={sortBy}
        setSortBy={setSortBy}
      />

      <BlogPostsGrid posts={displayedPosts} lang={lang} />
    </div>
  );
}

function BlogHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="text-center md:text-left space-y-2 border-b border-border/60 pb-5">
      <h3 className="text-3xl font-extrabold text-brand-primary flex items-center gap-2 justify-center md:justify-start">
        <BookOpen className="h-7 w-7 text-brand-accent" />
        {title}
      </h3>
      <p className="text-sm text-muted-foreground max-w-2xl">
        {subtitle}
      </p>
    </div>
  );
}

function BlogPostsGrid({ posts, lang }: { posts: BlogPost[]; lang: Language }) {
  if (posts.length === 0) {
    return (
      <div className="text-center py-16 border border-dashed rounded-2xl border-border/60 bg-card/25">
        <p className="text-sm text-muted-foreground font-semibold">
          {lang === "tr" ? "Arama kriterlerine uygun blog yazısı bulunamadı." : "No blog articles found matching your criteria."}
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {posts.map((post) => (
        <BlogPostCard key={post.slug} post={post} lang={lang} />
      ))}
    </div>
  );
}

export default BlogTab;
